/**
 * Workflows Registry
 * Central registry of all technical workflows available on InventiveHQ.com
 */

export interface Workflow {
  title: string;
  slug: string;
  description: string;
  category: string;
  toolCount: number;
  readingTime: number;
  audience: string[];
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  published: boolean;
  blogSlug?: string; // Link to corresponding blog article
  icon: string;
}

export const workflows: Workflow[] = [
  // Published Workflows
  {
    title: 'Web Application Security Audit',
    slug: 'web-application-security-audit',
    description: 'Master the 7-stage security audit process from reconnaissance to remediation using OWASP methodology',
    category: 'Security',
    toolCount: 15,
    readingTime: 22,
    audience: ['Security Engineers', 'Web Developers', 'DevSecOps', 'Penetration Testers'],
    difficulty: 'Advanced',
    published: true,
    blogSlug: 'web-application-security-audit-workflow',
    icon: '🔒',
  },
  {
    title: 'Incident Response & Forensics Investigation',
    slug: 'incident-response-forensics',
    description: 'NIST SP 800-61r3 aligned incident response workflow with forensics analysis and malware investigation',
    category: 'Security',
    toolCount: 12,
    readingTime: 18,
    audience: ['SOC Analysts', 'Incident Responders', 'Security Engineers', 'Forensics Specialists'],
    difficulty: 'Advanced',
    published: true,
    blogSlug: 'incident-response-forensics-workflow',
    icon: '🚨',
  },
  {
    title: 'API Security Testing',
    slug: 'api-security-testing',
    description: 'Complete API security testing workflow covering OWASP API Security Top 10 with authentication and authorization testing',
    category: 'Security',
    toolCount: 14,
    readingTime: 20,
    audience: ['API Developers', 'Backend Engineers', 'Security Engineers', 'QA Teams'],
    difficulty: 'Intermediate',
    published: true,
    blogSlug: 'api-security-testing-workflow',
    icon: '🔌',
  },
  {
    title: 'Email Security Hardening',
    slug: 'email-security-hardening',
    description: '13-week deployment plan for SPF, DKIM, and DMARC email authentication to prevent spoofing and improve deliverability',
    category: 'Security',
    toolCount: 9,
    readingTime: 19,
    audience: ['Email Administrators', 'Security Engineers', 'IT Managers', 'MSPs'],
    difficulty: 'Intermediate',
    published: true,
    blogSlug: 'email-security-hardening-workflow',
    icon: '📧',
  },
  {
    title: 'Network Troubleshooting & Performance Optimization',
    slug: 'network-troubleshooting',
    description: 'Systematic network troubleshooting using the OSI model approach with DNS, routing, and TCP optimization',
    category: 'Network',
    toolCount: 11,
    readingTime: 16,
    audience: ['Network Engineers', 'System Administrators', 'DevOps Engineers', 'MSPs'],
    difficulty: 'Intermediate',
    published: true,
    blogSlug: 'network-troubleshooting-workflow',
    icon: '🌐',
  },
  {
    title: 'Infrastructure as Code Security',
    slug: 'infrastructure-as-code-security',
    description: 'Secure Terraform workflows with policy validation, change management, and production deployment best practices',
    category: 'DevOps',
    toolCount: 8,
    readingTime: 24,
    audience: ['DevOps Engineers', 'Platform Engineers', 'SREs', 'Cloud Architects'],
    difficulty: 'Advanced',
    published: true,
    blogSlug: 'infrastructure-as-code-security-workflow',
    icon: '⚙️',
  },
  {
    title: 'Developer Debugging Workflow',
    slug: 'developer-debugging',
    description: 'Systematic debugging approach for API errors, data transformation, and production issues with log analysis',
    category: 'Development',
    toolCount: 13,
    readingTime: 18,
    audience: ['Software Developers', 'Full-Stack Engineers', 'DevOps Engineers', 'QA Engineers'],
    difficulty: 'Intermediate',
    published: true,
    blogSlug: 'developer-debugging-workflow',
    icon: '🐛',
  },

  // Additional Published Workflows
  {
    title: 'SSL/TLS Certificate Lifecycle Management',
    slug: 'ssl-tls-certificate-lifecycle',
    description: 'Complete certificate management from CSR generation to renewal automation with monitoring and emergency revocation',
    category: 'Security',
    toolCount: 8,
    readingTime: 20,
    audience: ['DevOps Engineers', 'System Administrators', 'Security Engineers'],
    difficulty: 'Intermediate',
    published: true,
    blogSlug: 'ssl-tls-certificate-lifecycle-management',
    icon: '🔐',
  },
  {
    title: 'Cloud Infrastructure Audit & Optimization',
    slug: 'cloud-infrastructure-audit',
    description: 'Post-migration validation, compliance assessment, cost optimization, and performance tuning for cloud workloads',
    category: 'Cloud',
    toolCount: 12,
    readingTime: 25,
    audience: ['Cloud Architects', 'Platform Engineers', 'Compliance Officers', 'CISOs'],
    difficulty: 'Advanced',
    published: true,
    blogSlug: 'cloud-infrastructure-audit-optimization',
    icon: '☁️',
  },
  {
    title: 'Malware Analysis & Reverse Engineering',
    slug: 'malware-analysis-reverse-engineering',
    description: 'Systematic malware analysis workflow with disassembly, deobfuscation, IOC extraction, and threat intelligence correlation',
    category: 'Security',
    toolCount: 11,
    readingTime: 22,
    audience: ['Security Researchers', 'SOC Analysts', 'Incident Responders', 'Forensics Teams'],
    difficulty: 'Advanced',
    published: true,
    blogSlug: 'malware-analysis-reverse-engineering-toolkit',
    icon: '🦠',
  },
  {
    title: 'Email Deliverability & Anti-Spoofing',
    slug: 'email-deliverability-anti-spoofing',
    description: 'Email infrastructure hardening and spoofing prevention across all vectors with homograph attack detection',
    category: 'Security',
    toolCount: 9,
    readingTime: 18,
    audience: ['Email Administrators', 'Security Engineers', 'Marketing Ops', 'MSPs'],
    difficulty: 'Intermediate',
    published: true,
    blogSlug: 'email-deliverability-anti-spoofing-campaign',
    icon: '🛡️',
  },
  {
    title: 'API Security & Rate Limiting Implementation',
    slug: 'api-security-rate-limiting',
    description: 'API protection with OAuth/OIDC implementation, rate limiting strategy, and webhook security validation',
    category: 'Development',
    toolCount: 10,
    readingTime: 19,
    audience: ['API Developers', 'Backend Engineers', 'Platform Teams', 'Security Engineers'],
    difficulty: 'Intermediate',
    published: true,
    blogSlug: 'api-security-rate-limiting-implementation',
    icon: '🚦',
  },
  {
    title: 'Compliance & Risk Assessment Program',
    slug: 'compliance-risk-assessment',
    description: 'Structured compliance verification and risk quantification across GDPR, HIPAA, SOC 2, and other frameworks',
    category: 'Compliance',
    toolCount: 11,
    readingTime: 21,
    audience: ['Compliance Officers', 'Risk Managers', 'Audit Teams', 'CISOs'],
    difficulty: 'Advanced',
    published: true,
    blogSlug: 'compliance-risk-assessment-program',
    icon: '📋',
  },
  {
    title: 'DevOps Log Analysis & Troubleshooting',
    slug: 'devops-log-analysis',
    description: 'Infrastructure troubleshooting through log parsing, config comparison, and timeline analysis',
    category: 'DevOps',
    toolCount: 10,
    readingTime: 17,
    audience: ['DevOps Engineers', 'SREs', 'Infrastructure Teams', 'Platform Engineers'],
    difficulty: 'Intermediate',
    published: true,
    blogSlug: 'devops-log-analysis-infrastructure-troubleshooting',
    icon: '📊',
  },

  // New Comprehensive Workflow Articles (Dec 2025)
  {
    title: 'Vulnerability Management & Patch Prioritization',
    slug: 'vulnerability-management-patch-prioritization',
    description: 'Complete 8-stage vulnerability management workflow with CVSS v4.0 scoring, CISA KEV integration, and risk-based patch prioritization',
    category: 'Security',
    toolCount: 19,
    readingTime: 16,
    audience: ['Security Engineers', 'System Administrators', 'IT Managers', 'Compliance Officers'],
    difficulty: 'Intermediate',
    published: true,
    blogSlug: 'vulnerability-management-patch-prioritization-workflow',
    icon: '🛡️',
  },
  {
    title: 'SOC Alert Triage & Investigation',
    slug: 'soc-alert-triage-investigation',
    description: 'MITRE ATT&CK aligned 6-stage SOC workflow for alert classification, threat intelligence enrichment, and incident investigation',
    category: 'Security',
    toolCount: 18,
    readingTime: 17,
    audience: ['SOC Analysts', 'Security Engineers', 'Incident Responders', 'Threat Hunters'],
    difficulty: 'Advanced',
    published: true,
    blogSlug: 'soc-alert-triage-investigation-workflow',
    icon: '🚨',
  },
  {
    title: 'Penetration Testing Methodology',
    slug: 'penetration-testing-methodology',
    description: 'Complete ethical hacking workflow following PTES framework with vulnerability exploitation and remediation validation',
    category: 'Security',
    toolCount: 20,
    readingTime: 30,
    audience: ['Penetration Testers', 'Security Consultants', 'Red Team Operators', 'Security Engineers'],
    difficulty: 'Advanced',
    published: true,
    blogSlug: 'penetration-testing-methodology-workflow',
    icon: '🎯',
  },
  {
    title: 'Data Breach Response & Notification',
    slug: 'data-breach-response-notification',
    description: 'GDPR 72-hour compliant breach response workflow with HIPAA notification, state law compliance, and forensic investigation',
    category: 'Security',
    toolCount: 26,
    readingTime: 28,
    audience: ['CISOs', 'Legal Teams', 'Compliance Officers', 'Incident Responders'],
    difficulty: 'Advanced',
    published: true,
    blogSlug: 'data-breach-response-notification-workflow',
    icon: '⚠️',
  },
  {
    title: 'Cloud Migration & Validation',
    slug: 'cloud-migration-validation',
    description: 'AWS/Azure/GCP migration workflow with 9-stage validation covering security, compliance, and performance optimization',
    category: 'Cloud',
    toolCount: 12,
    readingTime: 18,
    audience: ['Cloud Architects', 'Migration Engineers', 'DevOps Teams', 'Platform Engineers'],
    difficulty: 'Advanced',
    published: true,
    blogSlug: 'cloud-migration-validation-workflow',
    icon: '☁️',
  },
  {
    title: 'Multi-Cloud Cost Optimization',
    slug: 'multi-cloud-cost-optimization',
    description: 'FinOps framework 8-stage workflow for visibility, waste reduction, right-sizing, and continuous cost governance',
    category: 'Cloud',
    toolCount: 12,
    readingTime: 22,
    audience: ['Cloud Architects', 'FinOps Teams', 'Platform Engineers', 'CFOs'],
    difficulty: 'Intermediate',
    published: true,
    blogSlug: 'multi-cloud-cost-optimization-workflow',
    icon: '💰',
  },
  {
    title: 'HIPAA Security Assessment & Gap Analysis',
    slug: 'hipaa-security-assessment',
    description: 'Healthcare compliance workflow with OCR audit preparation, Security Rule requirements, and 2025 HHS updates',
    category: 'Compliance',
    toolCount: 12,
    readingTime: 18,
    audience: ['Healthcare IT', 'Compliance Officers', 'Privacy Officers', 'CISOs'],
    difficulty: 'Advanced',
    published: true,
    blogSlug: 'hipaa-security-assessment-workflow',
    icon: '🏥',
  },
  {
    title: 'SOC 2 Readiness & Audit Preparation',
    slug: 'soc2-readiness-audit-prep',
    description: 'Complete SOC 2 certification workflow covering Trust Service Criteria, Type I vs Type II, and evidence collection',
    category: 'Compliance',
    toolCount: 12,
    readingTime: 17,
    audience: ['SaaS Companies', 'Compliance Teams', 'IT Managers', 'Audit Teams'],
    difficulty: 'Advanced',
    published: true,
    blogSlug: 'soc2-readiness-audit-prep-workflow',
    icon: '📜',
  },
  {
    title: 'PCI DSS Compliance Validation',
    slug: 'pci-dss-compliance-validation',
    description: 'PCI DSS 4.0 workflow with merchant classification, SAQ completion, and cardholder data security validation',
    category: 'Compliance',
    toolCount: 16,
    readingTime: 24,
    audience: ['Payment Processing', 'E-commerce', 'IT Security', 'QSAs'],
    difficulty: 'Advanced',
    published: true,
    blogSlug: 'pci-dss-compliance-validation-workflow',
    icon: '💳',
  },
  {
    title: 'CI/CD Pipeline Security',
    slug: 'cicd-pipeline-security',
    description: 'DevSecOps workflow with SLSA framework, supply chain security, and automated security testing integration',
    category: 'DevOps',
    toolCount: 11,
    readingTime: 18,
    audience: ['DevOps Engineers', 'Security Engineers', 'Platform Teams', 'SREs'],
    difficulty: 'Intermediate',
    published: true,
    blogSlug: 'cicd-pipeline-security-workflow',
    icon: '🔄',
  },
  {
    title: 'Kubernetes Security & Hardening',
    slug: 'kubernetes-security-hardening',
    description: 'CIS Kubernetes Benchmark workflow with NSA/CISA guidance, pod security standards, and RBAC implementation',
    category: 'DevOps',
    toolCount: 12,
    readingTime: 17,
    audience: ['Kubernetes Admins', 'Platform Engineers', 'Security Engineers', 'SREs'],
    difficulty: 'Advanced',
    published: true,
    blogSlug: 'kubernetes-security-hardening-workflow',
    icon: '☸️',
  },
  {
    title: 'Disaster Recovery Testing & Validation',
    slug: 'disaster-recovery-testing',
    description: 'Complete DR testing workflow with RTO/RPO validation, failover execution, and business continuity verification',
    category: 'DevOps',
    toolCount: 20,
    readingTime: 22,
    audience: ['SREs', 'Infrastructure Teams', 'Business Continuity', 'IT Directors'],
    difficulty: 'Advanced',
    published: true,
    blogSlug: 'disaster-recovery-testing-workflow',
    icon: '🔥',
  },
  {
    title: 'Webhook Security Implementation',
    slug: 'webhook-security-implementation',
    description: 'HMAC signature validation, replay attack prevention, and OWASP API Security Top 10 compliant webhook implementation',
    category: 'Development',
    toolCount: 14,
    readingTime: 16,
    audience: ['Backend Developers', 'API Engineers', 'Security Engineers', 'Platform Teams'],
    difficulty: 'Intermediate',
    published: true,
    blogSlug: 'webhook-security-implementation-workflow',
    icon: '🔗',
  },
  {
    title: 'Secure Password & Authentication Flow',
    slug: 'secure-password-authentication-flow',
    description: 'NIST 800-63B compliant authentication workflow with password hashing, MFA, session management, and brute force protection',
    category: 'Development',
    toolCount: 10,
    readingTime: 22,
    audience: ['Full-Stack Developers', 'Security Engineers', 'Backend Engineers', 'Product Teams'],
    difficulty: 'Intermediate',
    published: true,
    blogSlug: 'secure-password-authentication-flow-workflow',
    icon: '🔐',
  },
];

// Helper functions
export function getAllWorkflows(): Workflow[] {
  return workflows;
}

export function getPublishedWorkflows(): Workflow[] {
  return workflows.filter(w => w.published);
}

export function getPlannedWorkflows(): Workflow[] {
  return workflows.filter(w => !w.published);
}

export function getWorkflowBySlug(slug: string): Workflow | undefined {
  return workflows.find(w => w.slug === slug);
}

export function getWorkflowsByCategory(category: string): Workflow[] {
  return workflows.filter(w => w.category === category);
}

export function getWorkflowCategories(): string[] {
  const categories = new Set(workflows.map(w => w.category));
  return Array.from(categories).sort();
}

export function getRelatedWorkflows(currentSlug: string, limit: number = 3): Workflow[] {
  const current = getWorkflowBySlug(currentSlug);
  if (!current) return [];

  // Find workflows in same category, excluding current
  const related = workflows
    .filter(w => w.slug !== currentSlug && w.category === current.category && w.published)
    .slice(0, limit);

  // If not enough in same category, add from other categories
  if (related.length < limit) {
    const additional = workflows
      .filter(w => w.slug !== currentSlug && w.category !== current.category && w.published)
      .slice(0, limit - related.length);

    return [...related, ...additional];
  }

  return related;
}
